// Descrizione di una regola archiviata del vecchio portale, nella stessa forma
// in cui l'editor mostra una regola viva (segnalazione del cliente
// OwlCaveLabs/syndesia-l2c-support#2).
//
// Il payload archiviato e' la regola come la restituiva `GET /api/rule/:id`:
// i valori stanno in `userValue`, `tagValue`, `value`... e portano il nome
// tecnico del campo (`template`, `sender`, `addresses`). Le etichette leggibili
// vivono invece nelle definizioni di condizioni e azioni. Idratare il payload
// con le stesse funzioni dell'editor le riporta al loro posto, e in piu'
// normalizza ogni valore in `value`: cosi' la descrizione si disegna con
// `RuleCustomValueDisplay`, lo stesso componente che l'editor usa gia', e le
// due pagine non possono divergere.
//
// Le condizioni escono come righe piatte con una profondita' invece che come
// albero: i blocchi annidati nelle 148 regole archiviate sono due, entrambi a
// un livello, e una lista piatta si disegna senza un componente ricorsivo.
//
// Niente Vue qui dentro: e' logica pura, pronta per i primi unit test quando il
// frontend avra' un runner.

import {
  RuleActionDefinition,
  RuleConditionDefinition,
  RuleCustomValue,
} from '@/types';
import { hydrateAction, hydrateCondition } from './ruleHelpers';

// `known` e' falso quando il codename non ha piu' una definizione: al
// 2026-09-16 non succede per nessuna delle regole archiviate, ma un'azione
// tolta dal codice in futuro deve comparire lo stesso, non sparire in silenzio
// come farebbe la sola idratazione, che scarta quello che non riconosce.
export interface DescribedItem {
  name: string;
  known: boolean;
  customValues: RuleCustomValue[];
}

export interface DescribedConditionRow extends DescribedItem {
  // Intestazione di un blocco AND/OR/NOT: non ha valori, apre le righe piu'
  // profonde che seguono.
  isBlock: boolean;
  depth: number;
}

export interface DescribedRule {
  // 'all' = devono valere tutte le condizioni, 'one' = ne basta una.
  when: 'all' | 'one';
  conditions: DescribedConditionRow[];
  actions: DescribedItem[];
}

const BLOCK_NAMES: Record<string, string> = {
  and: 'Blocco E (tutte)',
  or: 'Blocco O (almeno una)',
  not: 'Blocco NON (nessuna)',
};

export function describeConditionRows(
  items: any[],
  definitions: RuleConditionDefinition[],
  depth = 0,
): DescribedConditionRow[] {
  const rows: DescribedConditionRow[] = [];

  (items || []).forEach((item) => {
    if (item && item.block) {
      rows.push({
        name: BLOCK_NAMES[item.block] || `Blocco ${String(item.block).toUpperCase()}`,
        known: true,
        customValues: [],
        isBlock: true,
        depth,
      });
      rows.push(...describeConditionRows(item.conditions || [], definitions, depth + 1));
      return;
    }

    const hydrated = hydrateCondition(item, definitions);
    rows.push({
      name: hydrated ? hydrated.name : item.codename,
      known: !!hydrated,
      customValues: hydrated ? hydrated.customValues || [] : [],
      isBlock: false,
      depth,
    });
  });

  return rows;
}

export function describeActions(
  items: any[],
  definitions: RuleActionDefinition[],
): DescribedItem[] {
  return (items || []).map((item) => {
    const hydrated = hydrateAction(item, definitions);
    return {
      name: hydrated ? hydrated.name : item.codename,
      known: !!hydrated,
      customValues: hydrated ? hydrated.customValues || [] : [],
    };
  });
}

// Le righe di `changes` nascono all'estrazione e nominavano l'azione con il suo
// codename: `azione notify.email.user: mittente ... sostituito con ...`. Dal
// 2026-09-16 l'estrazione scrive gia' l'etichetta, ma i dati importati prima
// restano com'erano, e il pacchetto non si rigenera (rifare l'estrazione
// riporterebbe dentro tutto quello che il cliente ha tolto). Qui il prefisso
// viene tradotto a video.
//
// Il pezzo prima dei due punti si prende **tutto** e poi si cerca fra i
// codename, invece di descriverne l'alfabeto: un primo tentativo con
// `[\w.]+` lasciava indietro `notify.email.elettrica-figlia.requester`, che
// ha un trattino, e la verifica che avevo scritto usava lo stesso schema,
// quindi non poteva accorgersene. Qui non si indovina niente: o il prefisso e'
// un codename che esiste, o la riga resta com'e'. Cosi' le righe gia' scritte
// con l'etichetta non vengono toccate.
const PREFISSO_AZIONE = /^azione ([^:]+):/;

export function describeChanges(
  changes: string[] | null | undefined,
  actionDefinitions: RuleActionDefinition[],
): string[] {
  return (changes || []).map((riga) => {
    const trovato = PREFISSO_AZIONE.exec(riga);
    if (!trovato) return riga;
    const definizione = actionDefinitions.find((d) => d.codename === trovato[1]);
    if (!definizione) return riga;
    return `azione ${definizione.name}:${riga.slice(trovato[0].length)}`;
  });
}

// `payload` e' il JSON gia' letto della voce d'archivio. Un payload rovinato
// non deve portarsi dietro la pagina: qui torna una descrizione vuota e chi
// disegna mostra il poco che c'e'.
export function describeArchivedRule(
  payload: any,
  conditionDefinitions: RuleConditionDefinition[],
  actionDefinitions: RuleActionDefinition[],
): DescribedRule {
  return {
    when: payload && payload.when === 'one' ? 'one' : 'all',
    conditions: describeConditionRows(
      (payload && payload.conditions) || [],
      conditionDefinitions,
    ),
    actions: describeActions(
      (payload && payload.actions) || [],
      actionDefinitions,
    ),
  };
}
